import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const StyledFailedToLoadArrowIllustration = styled.svg`
  --color-white: ${({ theme }) => theme.color.additional.light.value};
  --color-gray-50: ${({ theme }) => theme.color.gray[50].value};
  --color-gray-100: ${({ theme }) => theme.color.gray[100].value};
  --color-gray-200: ${({ theme }) => theme.color.gray[200].value};
  --color-gray-300: ${({ theme }) => theme.color.gray[300].value};
  --color-gray-400: ${({ theme }) => theme.color.gray[400].value};
  --color-gray-500: ${({ theme }) => theme.color.gray[500].value};
  --color-gray-600: ${({ theme }) => theme.color.gray[600].value};
  --color-gray-700: ${({ theme }) => theme.color.gray[700].value};
  --color-gray-800: ${({ theme }) => theme.color.gray[800].value};
  --color-gray-900: ${({ theme }) => theme.color.gray[900].value};

  .grass-1,
  .grass-2,
  .tree {
    fill: var(--color-gray-100);
  }

  .help,
  .loader,
  .outer-close-path {
    fill: var(--color-gray-200);
  }

  .vector,
  .inner-close-path {
    fill: var(--color-gray-400);
  }
`;

const FailedToLoadArrowIllustration = ({
  className = undefined,
  dataTestId = undefined,
  size = 400,
  svgTitle = 'Failed to Load Arrow',
  ...other
}) => {
  return (
    <StyledFailedToLoadArrowIllustration
      className={className}
      data-testid={dataTestId ? `${dataTestId}-failed-to-load-arrow-svg` : 'failed-to-load-arrow-svg'}
      fill="none"
      height={size}
      viewBox="0 0 400 400"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...other}
    >
      {svgTitle && <title>{svgTitle}</title>}
      <g className="illustration-access-failed-to-load-arrow">
        <g className="ground">
          <path
            className="ellipse-20"
            opacity="0.3"
            d="M368 352C368 352 284 352 200 352C116 352 32 352 32 352C32 352 72.32 272 200 272C325.16 272 368 352 368 352Z"
            fill="url(#ground_gradient_fill_rule)"
          />
          <path
            className="grass-2"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M311.988 295.155C312.074 294.88 312.147 294.606 312.206 294.332C313.143 289.965 310.983 283.863 310.27 283.196C309.31 283.463 305.026 287.029 303.787 291.697C302.85 296.064 304.654 301.832 305.723 302.833C305.723 302.833 305.723 302.833 305.724 302.833C305.707 302.942 305.702 303.039 305.708 303.123C307.186 304.084 313.649 304.147 317.381 302.222C321.453 300.121 324.354 294.811 324.288 293.904C323.313 293.414 317.159 292.722 312.615 294.805C312.403 294.914 312.194 295.031 311.988 295.155Z"
          />
          <path
            className="tree"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M88.8749 292.925C88.7718 294.302 88.6745 295.646 88.5798 296.954C87.6037 310.441 86.9117 320 83.1932 320C80.9484 320 80.1408 313.726 79.2941 300.953C77.8338 300.902 70.5831 299.934 68.8364 293.649C67.4023 288.488 67.6496 276.338 70.8624 276.338C72.9741 276.338 73.2814 278.945 73.6429 282.012C74.0884 285.791 74.6161 290.268 78.7047 291.424C78.6955 291.27 78.6863 291.116 78.677 290.961C78.677 288.178 78.6206 285.268 78.5644 282.365L78.5644 282.365C78.3075 269.102 78.0537 256 83.1932 256C87.304 256 88.4663 265.228 88.7796 275.952C92.6072 275.19 93.1673 270.85 93.6779 266.894C94.1411 263.304 94.5636 260.03 97.3484 260.03C101.121 260.03 100.239 272.24 98.5553 277.447C96.6205 283.429 90.8684 285.481 88.8891 285.784C88.8883 286.953 88.885 288.112 88.8818 289.253V289.253V289.254C88.8783 290.505 88.8749 291.732 88.8749 292.925Z"
          />
          <path
            className="grass-1"
            d="M72.2723 307.805C74.185 311.828 74.3596 318.537 73.5248 320C72.2723 320 67.6804 316.585 65.5932 312.195C63.5059 307.317 63.9234 300.976 64.3409 300C65.1757 300 70.185 303.415 72.2723 307.805Z"
          />
        </g>
        <g className="secondary-item">
          <path
            className="help"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M110.616 308.493C102.547 312.084 98.915 321.548 102.508 329.616C106.101 337.684 115.566 341.317 123.634 337.725C131.703 334.133 135.335 324.67 131.742 316.601C128.149 308.533 118.684 304.901 110.616 308.493ZM123.143 332.689L120.219 333.99L118.917 331.066L121.841 329.765L123.143 332.689ZM121.125 320.015L120.408 321.946C119.831 323.481 119.564 324.651 120.54 326.843L117.617 328.145L117.291 327.414C116.575 325.806 116.582 324.052 117.159 322.516L118.152 319.868C118.458 319.101 118.454 318.227 118.096 317.423C117.38 315.815 115.479 315.085 113.871 315.801C112.263 316.517 111.533 318.418 112.249 320.026L109.326 321.327C107.888 318.097 109.339 314.316 112.569 312.878C115.799 311.44 119.581 312.891 121.019 316.121C121.592 317.408 121.587 318.811 121.125 320.015Z"
          />
          <path
            className="cloud"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M336.615 97.1525H336.576L336.038 97.1849C334.48 85.2446 324.319 76 312 76C302.252 76 293.917 81.7938 290.037 90.1215C285.338 90.3369 280.876 92.3029 277.536 95.6518C273.991 99.2054 272 104.025 272 109.051C272 114.076 273.991 118.896 277.536 122.45C279.291 124.21 281.375 125.605 283.668 126.558C285.961 127.51 288.419 128 290.901 128H336.615C340.696 128 344.609 126.375 347.494 123.482C350.379 120.59 352 116.667 352 112.576C352 108.486 350.379 104.563 347.494 101.67C344.609 98.7775 340.696 97.1525 336.615 97.1525ZM328.264 102V106.847H336.615C338.131 106.847 339.584 107.451 340.656 108.525C341.728 109.6 342.33 111.057 342.33 112.576C342.33 114.096 341.728 115.553 340.656 116.627C339.584 117.702 338.131 118.305 336.615 118.305H290.901C288.453 118.305 286.105 117.33 284.374 115.595C282.643 113.859 281.67 111.505 281.67 109.051C281.67 106.596 282.643 104.243 284.374 102.507C286.105 100.772 288.453 99.7966 290.901 99.7966C292.037 99.7966 293.15 100.012 294.178 100.387L295.672 100.931L295.93 99.3591C297.199 91.6281 303.869 85.6949 312 85.6949C316.313 85.6949 320.45 87.4128 323.5 90.4706C326.55 93.5284 328.264 97.6756 328.264 102ZM93.2308 122.644H93.2031L92.8265 122.666C91.7359 114.4 84.6231 108 76 108C69.1764 108 63.3421 112.011 60.6258 117.776C57.3369 117.926 54.2135 119.287 51.8752 121.605C49.394 124.065 48 127.402 48 130.881C48 134.361 49.394 137.697 51.8752 140.158C53.1038 141.376 54.5623 142.342 56.1676 143.001C57.7728 143.661 59.4933 144 61.2308 144H93.2308C96.087 144 98.8261 142.875 100.846 140.872C102.865 138.87 104 136.154 104 133.322C104 130.49 102.865 127.774 100.846 125.772C98.8261 123.769 96.0869 122.644 93.2308 122.644ZM87.3846 126V129.356H93.2308C94.2916 129.356 95.3091 129.774 96.0592 130.518C96.8093 131.261 97.2308 132.27 97.2308 133.322C97.2308 134.374 96.8093 135.383 96.0592 136.126C95.3091 136.87 94.2916 137.288 93.2308 137.288H61.2308C59.5171 137.288 57.8735 136.613 56.6618 135.412C55.45 134.21 54.7692 132.581 54.7692 130.881C54.7692 129.182 55.45 127.553 56.6618 126.351C57.8735 125.15 59.5171 124.475 61.2308 124.475C62.0257 124.475 62.8053 124.624 63.5245 124.883L64.5702 125.26L64.7508 124.172C65.6391 118.819 70.3083 114.712 76 114.712C79.0194 114.712 81.9151 115.901 84.0501 118.018C86.1852 120.135 87.3846 123.006 87.3846 126Z"
            fill="var(--color-gray-50)"
          />
        </g>
        <g className="loader-primary-item">
          <path
            className="loader"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M222.834 173.357L239.67 156.36L236.049 153.393C224.086 143.587 209.274 137.091 192.995 135.467L188.312 135V158.994L192.05 159.456C202.436 160.74 211.915 164.876 219.852 171.041L222.834 173.357ZM263.41 196.515L276.477 193.785L258.117 222.024L229.987 203.497L239.99 201.407C238.002 193.949 234.522 187.095 229.901 181.116L227.603 178.142L244.561 161.261L247.534 164.907C254.953 174.003 260.471 184.741 263.41 196.515ZM181.503 297.322V273.333L177.769 272.869C149.607 269.366 127.85 245.344 127.85 216.162C127.85 186.981 149.607 162.958 177.769 159.456L181.503 158.991V135.002L176.822 135.467C135.95 139.524 104 174.078 104 216.162C104 258.246 135.95 292.801 176.822 296.857L181.503 297.322Z"
          />
          <path
            className="vector"
            d="M201.955 196.409C206.037 196.409 209.609 199.973 209.609 204.046C209.609 208.12 206.037 211.684 201.955 211.684C197.873 211.684 194.302 208.12 194.302 204.046C194.302 199.973 197.873 196.409 201.955 196.409ZM173.892 204.046C173.892 208.12 170.32 211.684 166.238 211.684C162.156 211.684 158.585 208.12 158.585 204.046C158.585 199.973 162.156 196.409 166.238 196.409C170.32 196.409 173.892 199.973 173.892 204.046ZM184.097 226.959C193.026 226.959 200.884 230.625 205.476 236.175L198.23 243.405C195.934 239.688 190.475 237.142 184.097 237.142C177.719 237.142 172.259 239.688 169.963 243.405L162.718 236.175C167.31 230.625 175.168 226.959 184.097 226.959Z"
          />
          <g className="close">
            <circle className="close-outer-circle" cx="248.849" cy="283.137" r="38.3424" fill="var(--color-white)" />
            <path
              className="outer-close-path"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M201.986 283.137C201.986 257.224 222.936 236.274 248.849 236.274C274.763 236.274 295.712 257.224 295.712 283.137C295.712 309.05 274.763 330 248.849 330C222.936 330 201.986 309.05 201.986 283.137ZM248.849 315.919C266.918 315.919 281.632 301.206 281.632 283.137C281.632 265.068 266.918 250.355 248.849 250.355C206.583 250.355 206.583 315.919 248.849 315.919Z"
            />
            <path
              className="inner-close-path"
              d="M249.27 274.612L261.481 262.384L269.602 270.517L257.391 282.745L269.602 294.974L261.481 303.107L249.27 290.878L237.058 303.107L228.937 294.974L241.148 282.745L228.879 270.517L237.001 262.384L249.27 274.612Z"
            />
          </g>
        </g>
      </g>
      <defs>
        <linearGradient
          className="ground-gradient"
          id="ground_gradient_fill_rule"
          x1="200"
          y1="272"
          x2="200"
          y2="352"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--color-gray-300)" />
          <stop offset="0.390625" stopColor="var(--color-gray-200)" />
          <stop offset="1" stopColor="var(--color-gray-200)" stopOpacity="0" />
        </linearGradient>
      </defs>
    </StyledFailedToLoadArrowIllustration>
  );
};

FailedToLoadArrowIllustration.propTypes = {
  /** Adds additional class to the component */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Adjusts the size of the svg based upon the width. The aspect ratio is locked. */
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Adds SVG accessibility title element  */
  svgTitle: PropTypes.string,
};

export { FailedToLoadArrowIllustration };
